import { AppEndPoints, MillInfo } from "./Types";
import { getEndpoints } from "./Util";

// `toggleQqtUserProfileEndpoint` switches the user-profile lookup from eGerdau
// (`egerdauCustomer`) to the QQT backend `GET /users/current`, which resolves the same payload
// server-side from the MuleSoft API. ON for localhost/DEV/QA, OFF for pre-prod and PROD.
//
// Turning it on is safe ahead of the backend credentials being provisioned
// (MULE_API_BASE_URL / MULE_API_AZURE_CLIENT_ID / MULE_API_AZURE_CLIENT_SECRET in
// backend/charts/qqt-backend/*-values.yaml — still placeholders outside DEV/QA base URLs). On an
// Okta/Entra session a failed lookup (404 unknown QQT user, 503 unconfigured, 502 upstream) is
// absorbed silently: the app falls back to the identity claims from `/auth/me` and carries on with
// the default soldTo. On the legacy eGerdau path there is no such fallback, so only flip it there
// once /users/current answers 200. See README "Feature toggles".
export const EndpointMapping = new Map<string, AppEndPoints>([
  [
    "localhost",
    {
      api: "http://localhost:8080",
      egerdauBaseUrl: "https://dev.egerdau.com",
      egerdauAuth: "https://dev.egerdau.com/authorizationserver/oauth/authorize",
      egerdauToken: "https://dev.egerdau.com/authorizationserver/oauth/token",
      egerdauCustomer: "https://dev.egerdau.com/rest/v2/egerdauNA/users/current",
      egerdauCallback: "https://dev-sales-quick-quote-web.gerdaugln.tech",
      toggleGenAiFileParsing: true,
      toggleUnnestedAPINewEndpoint: true,
      toggleQqtUserProfileEndpoint: true,
    },
  ],
  [
    "dev-sales-quick-quote-web.gerdaugln.tech",
    {
      api: "https://qqt-api.dev.gerdaugln.tech",
      egerdauBaseUrl: "https://dev.egerdau.com",
      egerdauAuth: "https://dev.egerdau.com/authorizationserver/oauth/authorize",
      egerdauToken: "https://dev.egerdau.com/authorizationserver/oauth/token",
      egerdauCustomer: "https://dev.egerdau.com/rest/v2/egerdauNA/users/current",
      egerdauCallback: "https://dev-sales-quick-quote-web.gerdaugln.tech",
      toggleGenAiFileParsing: true,
      toggleUnnestedAPINewEndpoint: true,
      toggleQqtUserProfileEndpoint: true,
    },
  ],
  [
    "qa-sales-quick-quote-web.gerdaugln.tech",
    {
      api: "https://qqt-api.qa.gerdaugln.tech",
      egerdauBaseUrl: "https://qa.egerdau.com",
      egerdauAuth: "https://qa.egerdau.com/authorizationserver/oauth/authorize",
      egerdauToken: "https://qa.egerdau.com/authorizationserver/oauth/token",
      egerdauCustomer: "https://qa.egerdau.com/rest/v2/egerdauNA/users/current",
      egerdauCallback: "https://qa-sales-quick-quote-web.gerdaugln.tech",
      toggleGenAiFileParsing: true,
      toggleUnnestedAPINewEndpoint: true,
      toggleQqtUserProfileEndpoint: true,
    },
  ],
  [
    "pre-prod-sales-quick-quote-web.gerdaugln.tech",
    {
      api: "https://sales-quick-quote-api.pre-prod.gerdaugln.tech",
      egerdauBaseUrl: "https://qa.egerdau.com",
      egerdauAuth: "https://qa.egerdau.com/authorizationserver/oauth/authorize",
      egerdauToken: "https://qa.egerdau.com/authorizationserver/oauth/token",
      egerdauCustomer: "https://qa.egerdau.com/rest/v2/egerdauNA/users/current",
      egerdauCallback: "https://qa-sales-quick-quote-web.gerdaugln.tech",
      toggleGenAiFileParsing: true,
      toggleUnnestedAPINewEndpoint: true,
      toggleQqtUserProfileEndpoint: false,
    },
  ],
  [
    "quickquote.egerdau.com",
    {
      api: "https://sales-quick-quote-api.gerdaugln.tech",
      egerdauBaseUrl: "https://egerdau.com",
      egerdauAuth: "https://egerdau.com/authorizationserver/oauth/authorize",
      egerdauToken: "https://egerdau.com/authorizationserver/oauth/token",
      egerdauCustomer: "https://egerdau.com/rest/v2/egerdauNA/users/current",
      egerdauCallback: "https://quickquote.egerdau.com",
      toggleGenAiFileParsing: true,
      toggleUnnestedAPINewEndpoint: true,
      toggleQqtUserProfileEndpoint: false,
    },
  ],
]);
export const LocaleString = "en-US";
export const UsDollars = Intl.NumberFormat(LocaleString, {
  style: "currency",
  currency: "USD",
});
export const LocaleOptions: Intl.DateTimeFormatOptions = {
  month: "2-digit",
  day: "2-digit",
  year: "numeric",
};
export const {
  api: ApiUrl,
  egerdauBaseUrl: EgerdauUrl,
  egerdauToken: AccessTokenUrl,
  egerdauCustomer: CustomerProfileUrl,
  egerdauAuth: AuthUrl,
  egerdauCallback: CallbackUrl,
  toggleGenAiFileParsing,
  toggleUnnestedAPINewEndpoint,
  toggleQqtUserProfileEndpoint,
} = getEndpoints(EndpointMapping);

export const MillMap = new Map<number, MillInfo>([
  [1323, { name: "Cartersville", cityState: "Cartersville, GA" }],
  [1327, { name: "Midlothian", cityState: "Midlothian, TX" }],
  [1330, { name: "Petersburg", cityState: "Petersburg, VA" }],
]);

export const CustomerKey = "customer";
export const TokenKey = "token";
export const EGerdauCookie = "qqt_param";
export const CodeParamKey = "code";
export const ClientId = "quick_quote";
export const ClientSecret = "P9Ysz!R6";
export const TemplateDownloadUrl = `${ApiUrl}/file/download_gerdau_template`;
export const HanaSoldToUrl = `${ApiUrl}/${import.meta.env.REACT_APP_SOLDTO}`;

// Backend-for-Frontend (BFF) SSO endpoints. All OAuth/OIDC orchestration happens on the
// backend; the browser only kicks off a flow and relies on the HttpOnly QQT_SESSION cookie.
export const AuthBaseUrl = `${ApiUrl}/auth`;
export const AuthProvidersUrl = `${AuthBaseUrl}/providers`;
export const AuthMeUrl = `${AuthBaseUrl}/me`;
export const AuthLogoutUrl = `${AuthBaseUrl}/logout`;
export const OktaStartUrl = `${AuthBaseUrl}/okta/start`;
export const AzureStartUrl = `${AuthBaseUrl}/azure/start`;
